import { createVisitorNameLabels } from './nameLabels';
import { Matrix4, Vector3 } from 'three';
import type { WorldInit } from '../app/initializeWorld';
import { createCitizenBatch } from '../world/life/citizens/citizenBatch';
import {
  visitorFigure,
  visitorGeometry,
  visitorArmGeometry,
  clothing,
  type Clothing,
} from '../ui/visitorAppearance';
import { createVisitorCandles } from './visitorCandles';
import { createVisitorMotionStore } from './visitorLocomotion';
import type { CitizenAsset } from '../world/life/citizens/citizenAsset';
import type { PeerPose } from './peers';

function avatarDrawing(init: WorldInit, capacity: number, name: string, showNames: () => boolean) {
  const shapes = visitorShapes(init.citizens);
  const batch = createCitizenBatch(
    init.materials.painted(),
    [...shapes.map((shape) => shape.geometry), ...shapes.map((shape) => shape.arm)],
    capacity,
    name
  );
  const names = createVisitorNameLabels(showNames, name, init.pipeline.annotations);
  init.scene.add(batch.mesh);
  init.pipeline.annotations.scene.add(names.group);
  const matrix = new Matrix4(),
    scale = new Vector3(1, 1, 1);
  const animation = createVisitorMotionStore(init.citizens);
  const candles = createVisitorCandles(
    init.citizens,
    init.materials.painted({ instanced: true }),
    capacity
  );
  init.scene.add(candles.mesh);
  return { batch, animation, matrix, scale, candles, shapes, names };
}

function drawVisitors(
  drawing: ReturnType<typeof avatarDrawing>,
  peers: readonly PeerPose[],
  seconds: number,
  night: number,
  bodyVisible: boolean,
  epochMs: number
): void {
  const { batch, candles, animation, shapes, matrix, scale, names } = drawing;
  batch.begin();
  candles.begin();
  animation.begin(peers);
  for (const peer of peers) {
    const shape = shapes.findIndex(
      (figure) => figure.character === peer.character && figure.clothing === peer.clothing
    );
    const figure = shapes[shape];
    if (!figure) throw new Error('Visitor figure is unavailable');
    const carrying = night > 0.01;
    const pose = animation.sample(peer, figure.idle, seconds, carrying, epochMs);
    matrix
      .makeRotationY(peer.yaw + Math.PI / 2)
      .scale(scale)
      .setPosition(peer.x, peer.y - (peer.mode === 'boat' ? 1.45 : 1.7), peer.z);
    if (bodyVisible || carrying)
      batch.draw(bodyVisible ? shape : shape + shapes.length, matrix, pose.from, pose.to, pose.mix);
    if (carrying) candles.draw(matrix, pose, shape * 0.73);
  }
  batch.end();
  candles.end();
  names.update(bodyVisible ? peers : []);
}

export function createVisitorAvatars(
  init: WorldInit,
  capacity = 128,
  name = 'online-visitors',
  showNames: () => boolean = () => true
) {
  const drawing = avatarDrawing(init, capacity, name, showNames);
  const { candles, animation, batch, names } = drawing;
  return {
    lights: candles.lights,
    update(
      peers: readonly PeerPose[],
      seconds: number,
      night = 0,
      bodyVisible = true,
      epochMs = Date.now()
    ): void {
      drawVisitors(drawing, peers, seconds, night, bodyVisible, epochMs);
    },
    diagnostics: () => animation.diagnostics(),
    dispose(): void {
      init.scene.remove(batch.mesh);
      init.pipeline.annotations.scene.remove(names.group);
      names.dispose();
      animation.clear();
      init.scene.remove(candles.mesh);
      candles.dispose();
      batch.mesh.dispose();
    },
  };
}

export function visitorShapes(asset: CitizenAsset) {
  const shapes = (['male', 'female'] as const).flatMap((character) => {
    const figure = visitorFigure(asset, character);
    /* Players can fill the view. Crowd-distance simplification removes the face and
       clothing silhouette, so both local and remote visitors retain the entry mesh. */
    const lod = figure.lods[0];
    const idle = asset.clips[figure.clips.idle];
    const walk = asset.clips[figure.clips.walk];
    if (!lod || !idle || !walk) throw new Error('Visitor figure is incomplete');
    return (Object.keys(clothing) as Clothing[]).map((choice) => ({
      geometry: visitorGeometry(asset, lod, character, choice),
      arm: visitorArmGeometry(asset, lod, character, choice),
      idle,
      walk,
      character,
      clothing: choice,
    }));
  });
  return shapes;
}
